import {
	Button,
	type ColumnDef,
	DataTable,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Spinner,
} from "@monbits/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	Check,
	CheckCircle2,
	Copy,
	PlayCircle,
	Search,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import walletProvider, {
	type CheckDriftResponse,
	type DriftResult,
	type SuspendedWallet,
} from "src/providers/wallet-provider";

// ---------------------------------------------------------------------------
// Dialog state machine
// ---------------------------------------------------------------------------

type DialogState =
	| { step: "closed" }
	| { step: "checking"; wallet: SuspendedWallet }
	| { step: "results"; wallet: SuspendedWallet; data: CheckDriftResponse }
	| { step: "reconciling"; wallet: SuspendedWallet; data: CheckDriftResponse }
	| { step: "error"; wallet: SuspendedWallet; message: string };

export function SuspendedWalletsList() {
	const queryClient = useQueryClient();
	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);
	const [dialog, setDialog] = React.useState<DialogState>({ step: "closed" });

	const { data, isLoading } = useQuery({
		queryKey: ["suspended-wallets"],
		queryFn: () => walletProvider.listSuspendedWallets(),
		refetchInterval: 30_000,
	});

	const wallets = data?.wallets ?? [];

	const handleCopyAddress = React.useCallback(
		(address: string, e: React.MouseEvent) => {
			e.stopPropagation();
			navigator.clipboard.writeText(address);
			setCopiedAddress(address);
			toast.success("Address copied to clipboard");
			setTimeout(() => setCopiedAddress(null), 2000);
		},
		[],
	);

	const handleOpenDialog = React.useCallback(
		async (wallet: SuspendedWallet) => {
			setDialog({ step: "checking", wallet });
			try {
				const result = await walletProvider.checkBalanceDrift(wallet.id);
				setDialog({ step: "results", wallet, data: result });
			} catch (err) {
				const message = err instanceof Error ? err.message : "Unknown error";
				setDialog({ step: "error", wallet, message });
			}
		},
		[],
	);

	const handleReconcileAndResume = React.useCallback(async () => {
		if (dialog.step !== "results") return;
		const { wallet, data } = dialog;
		setDialog({ step: "reconciling", wallet, data });

		try {
			const result = await walletProvider.reconcileAndResume(wallet.id);
			const adjCount = result.adjustments.length;
			setDialog({ step: "closed" });
			toast.success("Wallet resumed", {
				description:
					adjCount > 0
						? `${adjCount} balance adjustment${adjCount !== 1 ? "s" : ""} applied. Transfers will resume on the next scheduler run.`
						: "No adjustments needed. Transfers will resume on the next scheduler run.",
			});
			queryClient.invalidateQueries({ queryKey: ["suspended-wallets"] });
			queryClient.invalidateQueries({ queryKey: ["inbound-wallets"] });
		} catch (err) {
			const message = err instanceof Error ? err.message : "Unknown error";
			setDialog({ step: "error", wallet, message });
		}
	}, [dialog, queryClient]);

	const columns: ColumnDef<SuspendedWallet>[] = React.useMemo(
		() => [
			{
				id: "network",
				header: "Network",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={row.blockchain} className="h-5 w-5" />
						<span className="whitespace-nowrap">
							{BLOCKCHAIN_NAMES[row.blockchain] || row.blockchain}
						</span>
					</div>
				),
			},
			{
				id: "address",
				header: "Address",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<span className="font-mono text-sm">{row.address}</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							onClick={(e) => handleCopyAddress(row.address, e)}
						>
							{copiedAddress === row.address ? (
								<Check className="h-3 w-3 text-green-600" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					</div>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: () => (
					<StatusBadge type="warning">Transfer Suspended</StatusBadge>
				),
			},
			{
				id: "reason",
				header: "Reason",
				cell: (row) => (
					<span
						className="text-sm text-muted-foreground max-w-xs truncate block"
						title={row.transferSuspendedReason}
					>
						{row.transferSuspendedReason || "\u2014"}
					</span>
				),
			},
			{
				id: "suspendedAt",
				header: "Suspended At",
				cell: (row) => <TimeLabel time={row.transferSuspendedAt} />,
			},
			{
				id: "actions",
				header: "",
				cell: (row) => (
					<Button
						variant="outline"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							handleOpenDialog(row);
						}}
					>
						<Search className="mr-1.5 h-3.5 w-3.5" />
						Check &amp; Resume
					</Button>
				),
			},
		],
		[copiedAddress, handleCopyAddress, handleOpenDialog],
	);

	return (
		<>
			{wallets.length > 0 && (
				<div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200/60 dark:border-amber-900/40">
					<p className="text-sm text-amber-800 dark:text-amber-200/80">
						<strong>
							{wallets.length} wallet{wallets.length !== 1 ? "s" : ""}
						</strong>{" "}
						suspended from automatic internal transfers.
					</p>
				</div>
			)}

			<DataTable
				columns={columns}
				data={wallets}
				isLoading={isLoading}
				getRowId={(row) => String(row.id)}
				emptyMessage="No suspended wallets"
				emptyDescription="All inbound wallets are operating normally"
			/>

			<ReconcileDialog
				state={dialog}
				onClose={() => setDialog({ step: "closed" })}
				onRetry={handleOpenDialog}
				onReconcileAndResume={handleReconcileAndResume}
			/>
		</>
	);
}

// ---------------------------------------------------------------------------
// ReconcileDialog — the full check & resume flow in a focused dialog
// ---------------------------------------------------------------------------

function ReconcileDialog({
	state,
	onClose,
	onRetry,
	onReconcileAndResume,
}: {
	state: DialogState;
	onClose: () => void;
	onRetry: (wallet: SuspendedWallet) => void;
	onReconcileAndResume: () => void;
}) {
	const isOpen = state.step !== "closed";
	const isLoading = state.step === "checking" || state.step === "reconciling";

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && !isLoading) onClose();
			}}
		>
			<DialogContent className="sm:max-w-lg">
				{state.step === "checking" && <CheckingContent />}
				{state.step === "results" && (
					<ResultsContent
						data={state.data}
						wallet={state.wallet}
						onResume={onReconcileAndResume}
						onClose={onClose}
					/>
				)}
				{state.step === "reconciling" && (
					<ReconcilingContent data={state.data} />
				)}
				{state.step === "error" && (
					<ErrorContent
						message={state.message}
						onRetry={() => onRetry(state.wallet)}
						onClose={onClose}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

function CheckingContent() {
	return (
		<>
			<DialogHeader>
				<DialogTitle>Checking balance drift</DialogTitle>
				<DialogDescription>
					Comparing ledger balances against on-chain values...
				</DialogDescription>
			</DialogHeader>
			<div className="flex items-center justify-center py-8">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		</>
	);
}

function ResultsContent({
	data,
	wallet,
	onResume,
	onClose,
}: {
	data: CheckDriftResponse;
	wallet: SuspendedWallet;
	onResume: () => void;
	onClose: () => void;
}) {
	const driftResults = data.results.filter((r) => r.hasDrift || r.error);

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{data.hasDrift ? "Balance drift detected" : "Balances in sync"}
				</DialogTitle>
				<DialogDescription>
					{data.hasDrift
						? `${driftResults.length} ${driftResults.length === 1 ? "currency has" : "currencies have"} drifted from on-chain values. Reconciliation will adjust the ledger to match.`
						: `All balances for ${BLOCKCHAIN_NAMES[wallet.blockchain] || wallet.blockchain} wallet match on-chain values. Safe to resume transfers.`}
				</DialogDescription>
			</DialogHeader>

			{data.hasDrift ? (
				<div className="my-4">
					<DriftTable results={driftResults} />
				</div>
			) : (
				<div className="flex items-center gap-2 py-6 justify-center text-sm text-green-600 dark:text-green-400">
					<CheckCircle2 className="size-5" />
					<span>No adjustments needed</span>
				</div>
			)}

			<DialogFooter>
				<Button variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button onClick={onResume}>
					{data.hasDrift ? (
						<>
							<PlayCircle className="mr-1.5 size-4" />
							Reconcile &amp; Resume
						</>
					) : (
						<>
							<PlayCircle className="mr-1.5 size-4" />
							Resume Transfers
						</>
					)}
				</Button>
			</DialogFooter>
		</>
	);
}

function ReconcilingContent({ data }: { data: CheckDriftResponse }) {
	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{data.hasDrift ? "Reconciling balances" : "Resuming transfers"}
				</DialogTitle>
				<DialogDescription>
					{data.hasDrift
						? "Adjusting ledger balances and resuming transfers..."
						: "Resuming automatic internal transfers..."}
				</DialogDescription>
			</DialogHeader>
			<div className="flex items-center justify-center py-8">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		</>
	);
}

function ErrorContent({
	message,
	onRetry,
	onClose,
}: {
	message: string;
	onRetry: () => void;
	onClose: () => void;
}) {
	return (
		<>
			<DialogHeader>
				<div className="flex items-center gap-3">
					<div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
						<AlertTriangle className="size-5 text-destructive" />
					</div>
					<DialogTitle>Something went wrong</DialogTitle>
				</div>
				<DialogDescription>{message}</DialogDescription>
			</DialogHeader>
			<DialogFooter className="mt-4">
				<Button variant="outline" onClick={onClose}>
					Close
				</Button>
				<Button variant="outline" onClick={onRetry}>
					Retry
				</Button>
			</DialogFooter>
		</>
	);
}

// ---------------------------------------------------------------------------
// DriftTable — clean, readable table showing currency-level drift
// ---------------------------------------------------------------------------

function DriftTable({ results }: { results: DriftResult[] }) {
	return (
		<div className="rounded-lg border overflow-hidden">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b bg-muted/40">
						<th className="px-3 py-2 text-left font-medium text-muted-foreground">
							Currency
						</th>
						<th className="px-3 py-2 text-right font-medium text-muted-foreground">
							Ledger
						</th>
						<th className="px-3 py-2 text-right font-medium text-muted-foreground">
							On-chain
						</th>
						<th className="px-3 py-2 text-right font-medium text-muted-foreground">
							Diff
						</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r) => (
						<tr
							key={`${r.currency}-${r.networkId}`}
							className="border-b last:border-0"
						>
							<td className="px-3 py-2 font-mono font-medium">{r.currency}</td>
							<td className="px-3 py-2 text-right font-mono tabular-nums">
								{r.error ? (
									<span className="text-muted-foreground">&mdash;</span>
								) : (
									r.dbAmount
								)}
							</td>
							<td className="px-3 py-2 text-right font-mono tabular-nums">
								{r.error ? (
									<span className="text-muted-foreground">&mdash;</span>
								) : (
									r.chainAmount
								)}
							</td>
							<td className="px-3 py-2 text-right font-mono tabular-nums">
								{r.error ? (
									<span
										className="text-destructive cursor-help"
										title={r.error}
									>
										Error
									</span>
								) : (
									<span className="text-amber-600 dark:text-amber-400">
										{r.diff}
									</span>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
